import React, { useState, useEffect } from 'react';
import { Container, Table, Badge, Button, Modal, Alert, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { Eye, CheckCircle, Reply } from 'lucide-react';
import contactMessageService from '../services/contactMessageService';

const ContactMessagesPage = () => {
  const { t } = useTranslation();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadMessages();
  }, []);

  const loadMessages = async () => {
    try {
      setLoading(true);
      const data = await contactMessageService.getMessages();
      setMessages(data.messages || []);
    } catch (error) {
      console.error('Failed to load messages:', error);
      setError('Failed to load contact messages');
    } finally {
      setLoading(false);
    }
  };

  const handleViewMessage = (message) => {
    setSelectedMessage(message);
    setShowModal(true);
  };

  const handleStatusUpdate = async (messageId, newStatus) => {
    try {
      setUpdating(true);
      await contactMessageService.updateMessageStatus(messageId, newStatus);
      await loadMessages(); // Reload messages
    } catch (error) {
      console.error('Failed to update status:', error);
      setError('Failed to update message status');
    } finally {
      setUpdating(false);
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      new: 'warning',
      read: 'info',
      replied: 'success'
    };
    
    const labels = {
      new: 'New',
      read: 'Read',
      replied: 'Replied'
    };

    return <Badge bg={variants[status]}>{labels[status]}</Badge>;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('tr-TR');
  };

  // Türkçe karakterleri decode et
  const decodeText = (text) => {
    if (!text) return '';
    try {
      // URL encoded karakterleri decode et
      return decodeURIComponent(text);
    } catch (error) {
      // Eğer decode edilemezse orijinal metni döndür
      return text;
    }
  };

  if (loading) {
    return (
      <Container fluid className="py-4">
        <div className="text-center">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>{t('admin.contact_messages.title')}</h2>
        <Button variant="outline-primary" onClick={loadMessages} disabled={loading}>
          Refresh
        </Button>
      </div>

      {error && (
        <Alert variant="danger" onClose={() => setError('')} dismissible>
          {error}
        </Alert>
      )}

      {messages.length === 0 ? (
        <Alert variant="info">
          {t('admin.contact_messages.no_messages')}
        </Alert>
      ) : (
        <Table responsive striped hover>
          <thead>
            <tr>
              <th>{t('admin.contact_messages.name')}</th>
              <th>{t('admin.contact_messages.email')}</th>
              <th>{t('admin.contact_messages.phone')}</th>
              <th>{t('admin.contact_messages.status')}</th>
              <th>{t('admin.contact_messages.date')}</th>
            </tr>
          </thead>
          <tbody>
            {messages.map((message) => (
              <tr key={message.id}>
                <td>{decodeText(message.full_name)}</td>
                <td>
                  <a href={`mailto:${message.email}`}>{message.email}</a>
                </td>
                <td>
                  {message.phone ? (
                    <a href={`tel:${message.phone}`}>{message.phone}</a>
                  ) : (
                    <span className="text-muted">-</span>
                  )}
                </td>
                <td>{getStatusBadge(message.status)}</td>
                <td>{formatDate(message.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      {/* Message Detail Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{t('admin.contact_messages.message_details')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedMessage && (
            <div>
              <div className="row mb-3">
                <div className="col-md-6">
                  <strong>{t('admin.contact_messages.name')}:</strong>
                  <p>{decodeText(selectedMessage.full_name)}</p>
                </div>
                <div className="col-md-6">
                  <strong>{t('admin.contact_messages.email')}:</strong>
                  <p>
                    <a href={`mailto:${selectedMessage.email}`}>{selectedMessage.email}</a>
                  </p>
                </div>
              </div>
              
              {selectedMessage.phone && (
                <div className="mb-3">
                  <strong>{t('admin.contact_messages.phone')}:</strong>
                  <p>
                    <a href={`tel:${selectedMessage.phone}`}>{selectedMessage.phone}</a>
                  </p>
                </div>
              )}
              
              <div className="mb-3">
                <strong>{t('admin.contact_messages.status')}:</strong>
                <p>{getStatusBadge(selectedMessage.status)}</p>
              </div>
              
              <div className="mb-3">
                <strong>{t('admin.contact_messages.date')}:</strong>
                <p>{formatDate(selectedMessage.created_at)}</p>
              </div>
              
              <div className="mb-3">
                <strong>{t('admin.contact_messages.message')}:</strong>
                <div className="border rounded p-3 bg-light">
                  <pre style={{ whiteSpace: 'pre-wrap', margin: 0, fontFamily: 'inherit' }}>
                    {decodeText(selectedMessage.message)}
                  </pre>
                </div>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            {t('common.close')}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default ContactMessagesPage; 